export interface Coordinates {
	latitude: number;
	longitude: number;
	accuracy: number;
	altitude: number | null;
	altitudeAccuracy: number | null;
	heading: number | null;
	speed: number | null;
}

export interface LocationResult {
	coords: Coordinates;
	timestamp: number;
}

export class LocationError extends Error {
	constructor(
		message: string,
		public readonly code: "PERMISSION_DENIED" | "POSITION_UNAVAILABLE" | "TIMEOUT" | "NOT_SUPPORTED"
	) {
		super(message);
		this.name = "LocationError";
	}
}

const DEFAULT_OPTIONS: PositionOptions = {
	enableHighAccuracy: true,
	timeout: 10000,
	maximumAge: 60000
};

export async function getLocation(options?: Partial<PositionOptions>): Promise<LocationResult> {
	if (!("geolocation" in navigator)) {
		throw new LocationError("Geolocation is not supported by this browser", "NOT_SUPPORTED");
	}

	const opts = { ...DEFAULT_OPTIONS, ...options };

	return new Promise((resolve, reject) => {
		navigator.geolocation.getCurrentPosition(
			(position) => {
				resolve({
					coords: {
						latitude: position.coords.latitude,
						longitude: position.coords.longitude,
						accuracy: position.coords.accuracy,
						altitude: position.coords.altitude,
						altitudeAccuracy: position.coords.altitudeAccuracy,
						heading: position.coords.heading,
						speed: position.coords.speed
					},
					timestamp: position.timestamp
				});
			},
			(error) => {
				switch (error.code) {
					case error.PERMISSION_DENIED:
						reject(new LocationError("Location permission denied", "PERMISSION_DENIED"));
						break;
					case error.POSITION_UNAVAILABLE:
						reject(new LocationError("Location information unavailable", "POSITION_UNAVAILABLE"));
						break;
					case error.TIMEOUT:
						reject(new LocationError("Location request timed out", "TIMEOUT"));
						break;
					default:
						reject(new LocationError("Unknown location error", "POSITION_UNAVAILABLE"));
				}
			},
			opts
		);
	});
}

export async function getLocationPermissionState(): Promise<PermissionState> {
	if (!("permissions" in navigator)) {
		return "prompt";
	}
	const result = await navigator.permissions.query({ name: "geolocation" });
	return result.state;
}
